
import { computed, defineComponent, provide, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import './signup-v2.css';
import {
    CONNECT_ROOM,
    createSignupForm,
    MOBILE_BANNERS,
    normalizeRoomCode,
    SHARE_METHOD,
    TELL_ADMIN,
    USE_DITTO,
    VALID_ROOM_CODE,
    WIZARD_STEPS,
    WIZARD_TOTAL,
    wizardIndexFromSlug
} from './signupFlow';
import { getSignupService } from './signupService';
import { getAuthInstance } from '../../../auth/authService';
import WizardVisual from './WizardVisual.vue';
import StepAboutYou from './steps/StepAboutYou.vue';
import StepOrganization from './steps/StepOrganization.vue';
import StepAccount from './steps/StepAccount.vue';
import StepVerifyEmail from './steps/StepVerifyEmail.vue';
import StepUseDitto from './steps/StepUseDitto.vue';
import StepTellAdmin from './steps/StepTellAdmin.vue';
import StepConnectRoom from './steps/StepConnectRoom.vue';
import ShareMethodScreen from './steps/ShareMethodScreen.vue';
import {
    PhBroadcast,
    PhBuildings,
    PhEnvelopeOpen,
    PhLock,
    PhPaperPlaneTilt
} from '@phosphor-icons/vue';

export default defineComponent({
    name: 'SignupWizard',
    components: {
        WizardVisual,
        StepAboutYou,
        StepOrganization,
        StepAccount,
        StepVerifyEmail,
        StepUseDitto,
        StepTellAdmin,
        StepConnectRoom,
        ShareMethodScreen,
        PhBroadcast,
        PhBuildings,
        PhEnvelopeOpen,
        PhLock,
        PhPaperPlaneTilt
    },
    setup() {
        const route = useRoute();
        const router = useRouter();

        // Sign-up is open at /signup. The launch gate (?sid=...) that sent
        // everyone else to the marketing trial page is gone. Links that still
        // carry sid are cleaned up in place: no reload, wizard state kept, and
        // every other parameter (pid, UTMs, HubSpot tracking) left as is.
        watch(
            () => route.query.sid,
            sid => {
                if (sid === undefined) return;
                const query = { ...route.query };
                delete query.sid;
                router.replace({ path: route.path, query });
            },
            { immediate: true }
        );

        // The /signup route has no auth guard (it held first render until the
        // Auth0 client finished loading, 10-15s cold). Same outcome, deferred:
        // once the client resolves, an authenticated user goes to the portal.
        const auth = getAuthInstance();
        const redirectIfAuthenticated = () => {
            if (!auth || auth.isLoading) {
                setTimeout(redirectIfAuthenticated, 500);
                return;
            }
            if (auth.isAuthenticated) router.replace('/');
        };
        redirectIfAuthenticated();

        const form = createSignupForm();
        form.pid = (route.query.pid as string) || null;
        provide('signupForm', form);
        provide('signupService', getSignupService());

        const slug = computed(() => {
            const step = route.params.step as string | undefined;
            const known =
                wizardIndexFromSlug(step) >= 0 ||
                [USE_DITTO, TELL_ADMIN, CONNECT_ROOM, SHARE_METHOD].includes(step || '');
            return known && step ? step : WIZARD_STEPS[0].slug;
        });

        // Step guard (Kyle, 2026-09-08): a direct load, refresh or browser Back
        // onto a later step arrives with empty state and dead-ends (Account posts
        // an empty email to Auth0; Organization gets "Email cannot be blank" on a
        // step with no email field). Each wizard step needs what the earlier
        // steps produced; without it, go back to the start. Side-flow screens
        // stay deep-linkable.
        function stepPrerequisitesMet(step: string): boolean {
            if (step === 'organization') return !!form.email && !!form.firstName;
            if (step === 'account' || step === 'verify-email') return !!form.registeredUserId;
            return true;
        }
        watch(
            slug,
            step => {
                if (!stepPrerequisitesMet(step)) {
                    router.replace({ path: `/signup/${WIZARD_STEPS[0].slug}`, query: route.query });
                }
            },
            { immediate: true }
        );

        const wizardIndex = computed(() => wizardIndexFromSlug(slug.value));
        const isWizardStep = computed(() => wizardIndex.value >= 0);
        const progressPercent = computed(() => ((wizardIndex.value + 1) / WIZARD_TOTAL) * 100);
        const banner = computed(() => MOBILE_BANNERS[slug.value]);
        const isConnectRoomBranded = computed(
            () => slug.value === CONNECT_ROOM && normalizeRoomCode(form.roomCode) === VALID_ROOM_CODE
        );

        function navigate(step: string) {
            router.push({ path: `/signup/${step}`, query: route.query });
        }

        function onWizardNext() {
            if (wizardIndex.value < WIZARD_TOTAL - 1) {
                navigate(WIZARD_STEPS[wizardIndex.value + 1].slug);
            } else {
                onWizardComplete();
            }
        }

        function onWizardBack() {
            if (wizardIndex.value > 0) {
                navigate(WIZARD_STEPS[wizardIndex.value - 1].slug);
            }
        }

        // Wizard complete — hand off to the branded login landing. The account
        // already exists at this point; sign-in stays blocked by the PostLogin
        // action until the user clicks the link in the verification email.
        function onWizardComplete() {
            router.push('/login');
        }

        return {
            slug,
            isWizardStep,
            progressPercent,
            banner,
            isConnectRoomBranded,
            navigate,
            onWizardNext,
            onWizardBack,
            onWizardComplete,
            USE_DITTO,
            TELL_ADMIN,
            CONNECT_ROOM,
            SHARE_METHOD,
            logoLight: require('../../../assets/images/signup-v2/ditto-mark-white.svg')
        };
    }
});
